import { useEffect, useState } from "react";
import { useJoinAndPay, useRenewSubscription, useSubscriptionQuote } from "@/shared/hooks";
import { hapticImpact, hapticNotify, showAlert } from "@/shared/telegram/tma";
import { renewMotivation } from "@/shared/texts/renew";
import { formatFp, formatRub } from "@/shared/utils/format";

/**
 * Flow Point: модалка оплаты подписки.
 *
 * Режимы (выбираются JoinButton / TodayPage через prop `mode`):
 * - `"full"` — новое вступление: показываем чекбокс согласия на подписку.
 * - `"resubscribe"` — подписка истекла, оплата нового периода: согласие
 *   подразумевается нажатием «Продлить» (чекбокс не нужен).
 * - `"renew"` — продление живой подписки в окне (этап 2, часть 3):
 *   `POST /payments/renew`, создаётся следующий период, серия сохраняется;
 *   согласие подразумевается нажатием кнопки.
 *
 * Суммы (цена, скидка по FP, к оплате, бонус) приходят из
 * `GET /api/v1/payments/quote` — считает СЕРВЕР той же функцией, что и
 * реальная оплата. Клиент ничего не считает сам: так расхождение
 * предпросмотра и списания невозможно (в этапе 3 к этой же формуле
 * добавится оффер).
 *
 * После оплаты ответ сервера (`outcome`) определяет, что показывать:
 * - `paid` — оплата прошла, период создан;
 * - `renewed` — продление прошло, создан следующий период;
 * - `subscription_active` — подписка уже была оплачена, ничего не списано;
 * - `rejoined` — вернулись в клуб по живой подписке, ничего не списано.
 */
export type JoinPayModalMode = "full" | "resubscribe" | "renew";

interface JoinPayModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Карточка клуба — id, title, penalty_amount, price_month. */
  habit: {
    id: string;
    title: string;
    penalty_amount: number;
    price_month: number;
  };
  /** Режим модалки (см. JoinPayModalMode). */
  mode: JoinPayModalMode;
  /**
   * Subtitle в шапке модалки (например, "Вступить в клуб" или
   * "Продлить подписку"). Если не передан — выбирается по mode.
   */
  subtitle?: string;
  /**
   * Текущая серия (дней подряд) — для текста продления «серия N дн.
   * не прервётся». До этапа 5 берётся из `checkin.streak_days`; само число
   * считает сервер, фронт только подставляет его в текст.
   */
  streakDays?: number;
}

/**
 * Генерация UUID4 для idempotency_key. На проде используется crypto.randomUUID()
 * (доступен во всех evergreen браузерах и Telegram WebView). На старых —
 * fallback на Math.random-based UUID v4 (RFC 4122 §4.4).
 */
function uuid4(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0;
    return v.toString(16);
  });
}

/**
 * Маппинг ошибок бэкенда на русские тексты для пользователя.
 */
function formatErrorMessage(code: string | undefined): string {
  switch (code) {
    case "habit_not_found":
      return "Клуб не найден.";
    case "habit_archived":
      return "Клуб архивирован.";
    case "habit_inactive":
      return "Клуб сейчас неактивен.";
    case "already_active":
      return "Ты уже в клубе.";
    case "habit_member_limit_reached":
      return "В клубе нет свободных мест. Попробуй позже.";
    case "subscription_required":
      return "Нужно согласие на подписку.";
    case "subscription_expired":
      return "Подписка закончилась — оплати новый период.";
    case "renew_not_available":
      return "Продление сейчас недоступно. Попробуй позже.";
    case "renew_subscription_expired":
      return "Подписка закончилась — оформи новую.";
    case "renew_membership_left":
      return "Вернись в клуб, чтобы продлить.";
    case "idempotency_conflict":
      return "Ошибка оплаты. Попробуй ещё раз.";
    default:
      return "Не удалось оплатить. Попробуй ещё раз.";
  }
}

/**
 * UI-хук для извлечения кода ошибки из ApiError.
 * Импортируется напрямую, чтобы избежать цикла с client.ts.
 */
type ApiErrorLike = {
  data?: { code?: string; message?: string };
};

function extractErrorCode(err: unknown): string | undefined {
  if (typeof err === "object" && err !== null && "data" in err) {
    const data = (err as ApiErrorLike).data;
    if (data && typeof data === "object" && typeof data.code === "string") {
      return data.code;
    }
  }
  return undefined;
}

export function JoinPayModal({
  open,
  onClose,
  onSuccess,
  habit,
  mode,
  subtitle,
  streakDays = 0,
}: JoinPayModalProps) {
  const [subscriptionAccepted, setSubscriptionAccepted] = useState(false);
  // Ключ идемпотентности фиксируется на время открытия модалки: повторный
  // так по той же кнопке не спишет деньги второй раз.
  const [idempotencyKey, setIdempotencyKey] = useState(() => uuid4());

  const quote = useSubscriptionQuote(habit.id, { enabled: open });
  const isRenew = mode === "renew";

  const onPaid = (data: import("@/shared/types").SubscribeResponse) => {
    hapticNotify("success");
    if (data.outcome === "subscription_active") {
      void showAlert(
        data.subscription_until
          ? `Подписка уже оплачена до ${data.subscription_until}. Оплата не требуется — докупи FP, чтобы продолжить.`
          : "Подписка уже оплачена. Оплата не требуется — докупи FP, чтобы продолжить.",
      );
    } else if (data.outcome === "rejoined") {
      void showAlert("Ты снова в клубе! Оплата не потребовалась.");
    } else {
      const paid = data.to_pay;
      const parts = isRenew
        ? [`Продлено! Списано ${formatRub(paid)}.`]
        : [`Готово! Списано ${formatRub(paid)}.`];
      parts.push(`Баланс: ${formatFp(data.new_balance_fp)}.`);
      if (data.fp_discount > 0) {
        parts.push(`Скидка по баллам: ${formatRub(data.fp_discount)}.`);
      }
      if (data.bonus_fp > 0) {
        parts.push(`Начислено ${formatFp(data.bonus_fp)} за оплату.`);
      }
      if (data.subscription_until) {
        parts.push(`Подписка активна до ${data.subscription_until}.`);
      }
      void showAlert(parts.join(" "));
    }
    onClose();
    onSuccess?.();
  };

  const subscribe = useJoinAndPay(isRenew ? undefined : onPaid);
  const renew = useRenewSubscription(isRenew ? onPaid : undefined);

  const isFullMode = mode === "full";
  const price = quote.data?.price ?? habit.price_month;
  const fpDiscount = quote.data?.fp_discount ?? 0;
  const toPay = quote.data?.to_pay ?? price;
  const bonusFp = quote.data?.bonus_fp ?? 0;
  const isQuoteLoading = open && quote.isLoading;
  const canPay = !isQuoteLoading && (isFullMode ? subscriptionAccepted : true);
  const isPending = isRenew ? renew.isPending : subscribe.isPending;

  // Escape закрывает (как в TopUpModal).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isPending) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, isPending]);

  // Сброс состояния при закрытии (и новый ключ идемпотентности на сессию).
  useEffect(() => {
    if (!open) {
      setSubscriptionAccepted(false);
      setIdempotencyKey(uuid4());
    }
  }, [open]);

  if (!open) return null;

  const handlePay = () => {
    if (!canPay) return;
    hapticImpact("medium");
    const payload = {
      habit_id: habit.id,
      // resubscribe/renew: согласие подразумевается нажатием «Продлить».
      // full: требуем явное согласие через чекбокс.
      subscription_accepted: isFullMode ? subscriptionAccepted : true,
      idempotency_key: idempotencyKey,
    };
    const onError = (err: unknown) => {
      const code = extractErrorCode(err);
      if (code === "already_active") {
        // Информационный успех: пользователь уже в клубе — это не ошибка
        // оплаты, а уже достигнутое состояние.
        hapticNotify("success");
        void showAlert(formatErrorMessage(code));
        onClose();
        onSuccess?.();
        return;
      }
      hapticNotify("error");
      void showAlert(formatErrorMessage(code));
    };
    if (isRenew) {
      renew.mutate(payload, { onError });
    } else {
      subscribe.mutate(payload, { onError });
    }
  };

  const titleText =
    subtitle ??
    (isFullMode
      ? `Вступить в клуб «${habit.title}»`
      : `Продлить подписку «${habit.title}»`);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="join-pay-title"
      onClick={() => !subscribe.isPending && onClose()}
    >
      <div
        className="w-full max-w-md rounded-t-2xl bg-canvas p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 id="join-pay-title" className="text-lg font-bold text-text">
            {titleText}
          </h2>
          <button
            onClick={onClose}
            aria-label="Закрыть"
            disabled={isPending}
            className="flex h-9 w-9 items-center justify-center rounded-full text-muted transition hover:bg-surface hover:text-text disabled:opacity-40"
          >
            ✕
          </button>
        </div>

        {/* Подписка: цена, скидка по баллам, бонус, итог. */}
        <div className="mb-4 rounded-card border border-white/10 bg-surface p-3">
          <div className="mb-1 text-[10px] uppercase tracking-wide text-muted">
            Подписка на 30 дней
          </div>
          <div className="mb-3 text-base font-semibold text-text">
            {formatRub(price)}
          </div>

          {fpDiscount > 0 && (
            <div className="mb-2 flex items-baseline justify-between text-sm">
              <span className="text-muted">Скидка за баллы</span>
              <span className="font-semibold text-success">
                −{formatRub(fpDiscount)}
              </span>
            </div>
          )}

          {bonusFp > 0 && (
            <div className="mb-2 flex items-baseline justify-between text-sm">
              <span className="text-muted">Бонус за оплату</span>
              <span className="font-semibold text-success">
                +{formatFp(bonusFp)}
              </span>
            </div>
          )}

          {isRenew ? (
            <p className="text-sm text-text">
              {renewMotivation(streakDays, fpDiscount > 0)}
            </p>
          ) : isFullMode ? (
            <label className="flex cursor-pointer items-start gap-2 text-sm text-text">
              <input
                type="checkbox"
                checked={subscriptionAccepted}
                onChange={(e) => {
                  hapticImpact("light");
                  setSubscriptionAccepted(e.target.checked);
                }}
                disabled={isPending}
                className="mt-0.5 h-4 w-4 rounded border-white/20 bg-surface text-primary focus:ring-primary disabled:opacity-40"
              />
              <span>Согласен на подписку {formatRub(price)} на 30 дней</span>
            </label>
          ) : (
            <p className="text-sm text-text">Оплата нового периода подписки.</p>
          )}
        </div>

        {/* Итог к оплате. */}
        <div className="mb-4 flex items-baseline justify-between border-t border-white/5 pt-3">
          <span className="text-sm text-muted">К оплате</span>
          <span className="text-lg font-bold text-text">
            {isQuoteLoading ? "…" : formatRub(toPay)}
          </span>
        </div>

        <button
          type="button"
          onClick={handlePay}
          disabled={!canPay || isPending}
          className="w-full rounded-card bg-primary px-4 py-3 text-sm font-semibold text-white transition active:scale-95 disabled:opacity-40"
        >
          {isQuoteLoading
            ? "Считаю…"
            : `Оплатить ${formatRub(toPay)}`}
        </button>

        {isPending && (
          <p className="mt-3 text-center text-xs text-muted">Зачисляю…</p>
        )}

        <div className="mt-4 rounded-card border border-primary/20 bg-primary/5 p-3 text-xs text-muted">
          💳 <strong className="text-text">Скоро:</strong> СБП, банковские карты,
          Telegram Stars.
        </div>
      </div>
    </div>
  );
}
